import { useMutation, useQuery } from "@apollo/client";
import { AnimatePresence } from "framer-motion";
import { take } from "lodash";
import moment from "moment";
import React, { useCallback, useState } from "react";
import DatePicker from "react-datepicker";
import {
  DeleteDraftMeetingNewPageMutationMutation,
  DeleteDraftMeetingNewPageMutationMutationVariables,
  GetUnscheduledMeetingsQueryQuery,
  GetUnscheduledMeetingsQueryQueryVariables,
} from "types/graphql-schema";

import deleteDraftMeetingMutation from "@apps/meeting-new/graphql/delete-draft-meeting-mutation";
import { currentUserVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import MotionDiv from "@components/motion/motion-div";
import { Select } from "@components/select/select";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  getUrl,
  getUrlUsingTypename,
} from "@helpers/helpers";

import MeetingOverview from "./components/meetings-overview";
import { MeetingType } from "./components/meetings-overview-constants";
import getUnscheduledMeetingsQuery from "./graphql/get-unscheduled-meetings-query";

const CalendarIcon = getTopicflowIcon("meeting");

const CalendarPickerButton = React.forwardRef<
  HTMLButtonElement,
  { onClick?: () => void }
>(({ onClick }, ref) => (
  <Button
    ref={ref}
    icon={CalendarIcon}
    theme={buttonTheme.iconGray}
    size={ButtonSize.medium}
    onClick={onClick}
    tooltip="Jump to date"
  />
));

const MeetingsPanel = ({
  onClose,
  compact = false,
  externalSelectedDay,
  onChangeSelectedDay,
  headerActions,
}: {
  onClose?: () => void;
  compact?: boolean;
  externalSelectedDay?: string;
  onChangeSelectedDay?: (day: string) => void;
  headerActions?: React.ReactNode;
}) => {
  const currentUser = currentUserVar();
  const [internalSelectedDay, setInternalSelectedDay] = useState(
    moment().format("YYYY-MM-DD"),
  );
  const selectedDay = externalSelectedDay || internalSelectedDay;
  const setSelectedDay = setInternalSelectedDay;
  const [forceTodayScroll, setForceTodayScroll] = useState(0);
  const [participantFilter, setParticipantFilter] = useState<number | null>(
    null,
  );
  const [meetingTypeFilter, setMeetingTypeFilter] = useState(MeetingType.ALL);
  const [showUnscheduledMeetings, setShowUnscheduledMeetings] = useState(false);

  const {
    options: assessmentUserFilterOptions,
    query: userQuery,
    setQuery: setUserQuery,
  } = useUserComboboxQuery({});
  const userComboboxValue =
    assessmentUserFilterOptions.find(({ id }) => id === participantFilter) ||
    null;

  const handleChangeParticipantFilter = useCallback(
    (option?: UserComboboxOption) => {
      setParticipantFilter(
        option && typeof option.id === "number" ? option.id : null,
      );
    },
    [],
  );
  const link = useLink();
  const { data } = useQuery<
    GetUnscheduledMeetingsQueryQuery,
    GetUnscheduledMeetingsQueryQueryVariables
  >(getUnscheduledMeetingsQuery, {
    variables: { forUserId: currentUser.id },
  });
  const unscheduledMeetings = data?.unscheduledMeetings
    ? assertEdgesNonNull(data.unscheduledMeetings)
    : [];

  const handleChangeSidebarMeetingId = (
    meetingId: number,
    meetingGroupId: number,
  ) => {
    const url = getUrl({ meetingGroupId, meetingId });
    link.redirect(url);
  };

  const [deleteDraftMeeting] = useMutation<
    DeleteDraftMeetingNewPageMutationMutation,
    DeleteDraftMeetingNewPageMutationMutationVariables
  >(deleteDraftMeetingMutation);

  const handleClickDeleteDraftMeeting = useCallback(
    (meetingId: number) => {
      if (
        window.confirm(
          "Are you sure you want to delete this unscheduled meeting?",
        )
      ) {
        deleteDraftMeeting({
          variables: { meetingId },
          refetchQueries: [getUnscheduledMeetingsQuery],
          onCompleted: () => {
            successNotificationVar({ title: "Meeting discarded." });
          },
        });
      }
    },
    [deleteDraftMeeting],
  );

  return (
    <div
      className={classNames(
        "text-gray-700 w-full flex flex-col",
        compact ? "" : "h-full",
      )}
    >
      {!compact && (
        <div className="border-b pl-5 pr-3 pt-4 pb-5 flex flex-col gap-2">
          <div className="flex gap-1 justify-between items-center mb-1">
            <div className="flex items-center gap-2">
              {onClose && (
                <Button
                  icon={getTopicflowIcon("close")}
                  theme={buttonTheme.iconGray}
                  onClick={() => {
                    onClose();
                  }}
                  size={ButtonSize.medium}
                />
              )}
              Meetings
            </div>
            <div className="flex items-center gap-0.5">
              <DatePicker
                selected={moment(selectedDay).toDate()}
                onChange={(date) => {
                  if (date) {
                    const newDay = moment(date).format("YYYY-MM-DD");
                    setSelectedDay(newDay);
                    onChangeSelectedDay?.(newDay);
                    setForceTodayScroll(Date.now());
                  }
                }}
                popperPlacement="bottom-end"
                popperClassName="z-tooltip"
                portalId="portal-react-datepicker"
                popperProps={{ strategy: "fixed" }}
                customInput={<CalendarPickerButton />}
              />
              <Button
                tooltip="Jump to Today"
                icon={getTopicflowIcon("today")}
                theme={buttonTheme.iconGray}
                size={ButtonSize.medium}
                onClick={() => {
                  const today = moment().format("YYYY-MM-DD");
                  setSelectedDay(today);
                  onChangeSelectedDay?.(today);
                  setForceTodayScroll(Date.now());
                }}
              />
              {headerActions}
            </div>
          </div>
          <UserCombobox
            onChangeValue={handleChangeParticipantFilter}
            options={assessmentUserFilterOptions}
            value={userComboboxValue}
            placeholder="All people"
            clearable={participantFilter !== null}
            query={userQuery}
            onChangeQuery={setUserQuery}
            onClearValue={handleChangeParticipantFilter}
            buttonProps={{ size: ButtonSize.medium }}
          />
          <Select
            value={meetingTypeFilter}
            options={[
              { value: MeetingType.ALL, label: "All meetings" },
              { value: MeetingType.ONE_ON_ONE, label: "1-on-1s" },
              { value: MeetingType.GROUP, label: "Group meetings" },
            ]}
            onChange={(opt) => setMeetingTypeFilter(opt.value)}
            aria-label="Meeting type filter"
            buttonProps={{ size: ButtonSize.medium }}
          />
        </div>
      )}
      <div className="relative flex-1">
        <MeetingOverview
          selectedDay={selectedDay}
          onClickMeetingLink={handleChangeSidebarMeetingId}
          forceTodayScroll={forceTodayScroll}
          compact={compact}
          participantFilter={compact ? null : participantFilter}
          meetingTypeFilter={compact ? MeetingType.ALL : meetingTypeFilter}
        />
      </div>
      {!compact && unscheduledMeetings.length > 0 && (
        <div className="border-t px-3 py-3">
          <div className="flex items-center justify-between gap-1">
            <div className="text-xs font-medium pl-2">
              Unscheduled meetings ({unscheduledMeetings.length})
            </div>
            <Button
              icon={getTopicflowIcon("edit")}
              size={ButtonSize.medium}
              theme={buttonTheme.iconGray}
              onClick={() => setShowUnscheduledMeetings(true)}
            />
          </div>
          {showUnscheduledMeetings && (
            <Modal onClose={() => setShowUnscheduledMeetings(false)} alignedTop>
              <div className="p-6 flex flex-col gap-4">
                <ModalTitle onClose={() => setShowUnscheduledMeetings(false)}>
                  Unscheduled Meetings
                </ModalTitle>
                <div className="flex flex-col text-gray-700 border rounded-md divide-y">
                  {unscheduledMeetings.map((unscheduledMeeting) => (
                    <div
                      className="px-4 py-2 hover:bg-gray-50 flex items-center min-w-0 overflow-hidden"
                      key={unscheduledMeeting.id}
                    >
                      {unscheduledMeeting.meeting ? (
                        <AppLink
                          className="flex-1 truncate"
                          to={getUrlUsingTypename(unscheduledMeeting.meeting)}
                          onClick={(e) => {
                            e.preventDefault();
                            if (unscheduledMeeting.meeting) {
                              handleChangeSidebarMeetingId(
                                unscheduledMeeting.meeting.id,
                                unscheduledMeeting.meeting.meetingGroupId,
                              );
                              setShowUnscheduledMeetings(false);
                            }
                          }}
                        >
                          {unscheduledMeeting.meeting?.title}
                        </AppLink>
                      ) : (
                        <span className="flex-1">Unscheduled Meeting</span>
                      )}
                      <div>
                        <Button
                          icon={getTopicflowIcon("trash")}
                          theme={buttonTheme.iconGray}
                          size={ButtonSize.medium}
                          onClick={() => {
                            if (unscheduledMeeting.meeting) {
                              handleClickDeleteDraftMeeting(
                                unscheduledMeeting.meeting.id,
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Modal>
          )}
          <div className="flex flex-col gap-px">
            <AnimatePresence>
              {take(unscheduledMeetings, 3).map((unscheduledMeeting) => {
                return (
                  <MotionDiv
                    key={unscheduledMeeting.id}
                    className=""
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="rounded hover:bg-nav-item-active px-2 py-1 text-sm text-gray-600">
                      {unscheduledMeeting.meeting ? (
                        <AppLink
                          className="w-full flex min-w-0 gap-2 items-center"
                          to={getUrlUsingTypename(unscheduledMeeting.meeting)}
                          onClick={(e) => {
                            e.preventDefault();
                            if (unscheduledMeeting.meeting) {
                              handleChangeSidebarMeetingId(
                                unscheduledMeeting.meeting.id,
                                unscheduledMeeting.meeting.meetingGroupId,
                              );
                            }
                          }}
                        >
                          <span className="flex-1 truncate">
                            {unscheduledMeeting.meeting?.title}
                          </span>
                          <span className="text-gray-500">-</span>
                        </AppLink>
                      ) : (
                        <div className="w-full flex gap-2 items-center">
                          <span className="flex-1">Unscheduled Meeting</span>
                          <span className="text-gray-500">-</span>
                        </div>
                      )}
                    </div>
                  </MotionDiv>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingsPanel;
